import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CSATMetrics {
  csatScore: number;
  totalResponses: number;
  satisfiedResponses: number;
  averageRating: number;
  uniqueGenerators: number;
}

export const useCSATMetrics = (daysRange: number = 30) => {
  return useQuery({
    queryKey: ['csat-metrics', daysRange],
    queryFn: async (): Promise<CSATMetrics> => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysRange);

      const { data, error } = await supabase
        .from('csat_responses')
        .select('rating, generator_id')
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      const totalResponses = data?.length || 0;
      
      if (totalResponses === 0) {
        return {
          csatScore: 0,
          totalResponses: 0,
          satisfiedResponses: 0,
          averageRating: 0,
          uniqueGenerators: 0,
        };
      }

      // Contar respostas satisfeitas (rating >= 4)
      const satisfiedResponses = data.filter(r => r.rating >= 4).length;
      
      // Calcular CSAT como percentual
      const csatScore = (satisfiedResponses / totalResponses) * 100;

      // Calcular rating médio
      const totalRating = data.reduce((sum, r) => sum + r.rating, 0);
      const averageRating = totalRating / totalResponses;

      // Contar geradores únicos
      const uniqueGeneratorIds = new Set(
        data.filter(r => r.generator_id).map(r => r.generator_id)
      );
      const uniqueGenerators = uniqueGeneratorIds.size;

      return {
        csatScore: Math.round(csatScore * 10) / 10,
        totalResponses,
        satisfiedResponses,
        averageRating: Math.round(averageRating * 10) / 10,
        uniqueGenerators,
      };
    },
  });
};
